/**
 * boring-issues.js — wat er mis is met een boring, en hoe erg dat is.
 *
 * Tot nu toe kreeg alles dezelfde amberkleur: een gat in het profiel las net zo dringend als
 * een ontbrekende boormeester. Daardoor gaat een lijst met meldingen ruis worden en kijkt
 * niemand er meer naar.
 *
 * Twee niveaus, met een scherpe grens:
 *
 *   BLOKKEREND  Hiermee kun je het onderzoek niet afronden. Het rapport of de export klopt
 *               niet, of is niet te maken. Dit MOET iemand oplossen.
 *
 *   AANDACHT    Het onderzoek kan door, maar er ontbreekt iets dat je meestal wél invult,
 *               of een waarde ziet er onwaarschijnlijk uit. Een mens beslist of het klopt.
 *
 * De grens ligt bij: kan de opdrachtgever hier een geldig rapport op baseren? Een boring
 * zonder coördinaat is niet te plaatsen en een monster zonder barcode niet te herleiden naar
 * het lab — die twee blokkeren. Een ontbrekende boormeester is vervelend maar verandert niets
 * aan de meetgegevens.
 *
 * Bewust een los bestand zonder afhankelijkheden: dit is domeinkennis, geen opmaak, en zo is
 * het te testen zonder browser en later te hergebruiken in de veldapp.
 */

export const BLOCKING = 'blocking';
export const WARNING = 'warning';

/** Onwaarschijnlijke meetwaarden. Buiten deze grenzen is het bijna altijd een typefout. */
const PLAUSIBLE = {
  ph: { min: 0, max: 14, label: 'pH' },
  temperature: { min: -5, max: 40, label: 'Temperatuur' },
  ec: { min: 0, max: 100000, label: 'EGV' },
};

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const isEmpty = (v) => v === null || v === undefined || String(v).trim() === '';

/**
 * Intensiteiten waarbij het bestanddeel de laag ís.
 *
 * Een laag van lucht, ballast of puin heeft geen grondsoort en hoort die ook niet te krijgen;
 * de melding 'zonder grondsoort' stond daar dan te knipperen bij iets wat gewoon klopt.
 *
 * NEN 6693 noemt dat 'overvloedig' (50 tot 80 % volume) en 'dominant' (80 tot 100 %), NEN 5104
 * 'uiterst' en 'volledig'. Beide standen staan hier, want een boring kan in beide standaarden
 * beschreven zijn, en de codes ook: 6693 bewaart de term, 5104 het nummer uit de TI-codelijst.
 *
 * De waarden staan hier uitgeschreven en niet via @mv/models, want dit bestand is met opzet
 * afhankelijkheidsvrij. De bron is `BZB_FILLS_LAYER` in @mv/models;
 * `tests/bzb-intensiteit-codes.test.mjs` in mv-backend-worker legt de twee naast elkaar, zodat
 * deze kopie niet stilletjes kan verouderen.
 */
const BZB_VULT_DE_LAAG = new Set([
  // NEN 6693 (term) en NEN 5104 (label)
  'overvloedig', 'dominant', 'volledig', 'uiterst',
  // Dezelfde vier als TI-code: uiterst=4, volledig=5, dominant=18, overvloedig=19
  '4', '5', '18', '19',
]);

/** Bestaat deze laag zelf uit een bijzonder bestanddeel? Dan hoort er geen grondsoort bij. */
const isBzbLaag = (layer) => (layer?.bzb || []).some((b) =>
  b && !isEmpty(b.type ?? b.code) && BZB_VULT_DE_LAAG.has(String(b.gradation ?? '').trim().toLowerCase()));

/**
 * Is dit een inspectiegat met boring (een uitgegraven sleuf)?
 *
 * Bewust hier uitgeschreven en niet via @mv/models: dit bestand is met opzet
 * afhankelijkheidsvrij, zodat het zonder browser te testen is en later in de veldapp kan.
 * Het accepteert dezelfde vormen als `normalizeBoringType`: de code 'IB' en het label -- er
 * staan historisch allebei in deze kolom.
 */
const isInspectiegat = (type) => {
  const t = String(type ?? '').trim().toLowerCase();
  return t === 'ib' || t === 'inspectiegat met boring';
};

/** Lagen op diepte, zonder de invoerlijst te muteren. */
const byDepth = (layers) =>
  [...(layers || [])].sort((a, b) => (num(a.upperDepth) ?? 0) - (num(b.upperDepth) ?? 0));

/**
 * Hoe je één laag benoemt: op zijn dieptetraject.
 *
 * Een laag heeft geen naam of nummer waar iemand iets aan heeft -- de volgorde in de lijst
 * zegt niets zodra je aan het bewerken bent geweest. De diepte is waar je hem op terugvindt,
 * zowel in het formulier als in het veld.
 */
const layerRange = (l) => {
  const van = num(l.upperDepth);
  const tot = num(l.lowerDepth);
  if (van === null && tot === null) return null;
  if (tot === null) return `vanaf ${van}`;
  if (van === null) return `tot ${tot}`;
  return `${van}\u2013${tot}`;
};

/**
 * De trajecten achter een melding, afgekapt.
 *
 * "3 lagen zonder kleur" vertelt je wat er mis is maar niet waar je moet zijn; met de dieptes
 * erbij loop je ze na zonder eerst de hele boring open te klappen. Bij meer dan drie kappen we
 * af: dan is de melding zelf het signaal, en ga je de boring toch in plaats voor plaats na.
 */
function ranges(layers, max = 3) {
  const list = byDepth(layers).map(layerRange).filter(Boolean);
  if (!list.length) return '';
  const shown = list.slice(0, max).join(', ');
  const rest = list.length - max;
  // De eenheid achter de laatste diepte, niet achter het restant: '60-100 +1 cm' leest alsof
  // er een centimeter bij komt.
  return rest > 0 ? ` (${shown} cm +${rest} meer)` : ` (${shown} cm)`;
}

/**
 * Gaten en overlappingen tussen opeenvolgende lagen.
 * Een gat betekent dat er een stuk boorprofiel onbeschreven is; dat is geen slordigheid maar
 * ontbrekende meetdata.
 */
function depthProblems(layers) {
  const out = [];
  const ls = byDepth(layers);
  for (let i = 0; i < ls.length - 1; i++) {
    const a = num(ls[i].lowerDepth);
    const b = num(ls[i + 1].upperDepth);
    if (a === null || b === null) continue;
    if (b > a) out.push({ type: 'gap', from: a, to: b });
    else if (b < a) out.push({ type: 'overlap', from: b, to: a });
  }
  return out;
}

/**
 * Alle problemen van één boring.
 *
 * @param {object} boring  platte boring incl. layers/monsters/peilbuizen/watermonsters
 * @param {{duplicateNames?: Set<string>, recordingStatus?: string|null}} [ctx]
 * @returns {Array<{code: string, severity: string, label: string}>} blokkerend eerst
 */
export function boringIssues(boring, ctx = {}) {
  if (!boring) return [];
  const out = [];
  const add = (severity, code, label) => out.push({ severity, code, label });

  const layers = (boring.layers || []);
  const monsters = (boring.monsters || []);
  const peilbuizen = (boring.peilbuizen || []);
  const watermonsters = (boring.watermonsters || []);

  // ── Blokkerend ─────────────────────────────────────────────────────────────

  // Zonder coördinaat is de boring niet te plaatsen: niet op de kaart, en in de export
  // ontbreekt de locatie waar het hele onderzoek aan hangt.
  if (num(boring.x) === null || num(boring.y) === null) {
    add(BLOCKING, 'no-coordinates', 'Geen coördinaten');
  }

  // Een opname die niet is verwerkt heeft geen gegevens opgeleverd; er is dus niets om op
  // te rapporteren, hoe volledig de rest ook lijkt.
  if (ctx.recordingStatus === 'error') {
    add(BLOCKING, 'recording-failed', 'Opname niet verwerkt');
  }

  if (!layers.length) {
    add(BLOCKING, 'no-layers', 'Geen lagen beschreven');
  }

  for (const p of depthProblems(layers)) {
    add(BLOCKING, p.type === 'gap' ? 'profile-gap' : 'profile-overlap',
      p.type === 'gap'
        ? `Gat in profiel, ${p.from} tot ${p.to} cm`
        : `Overlap in profiel, ${p.from} tot ${p.to} cm`);
  }

  // Een laag die uit een bestanddeel bestaat (lucht, ballast, puin) telt niet mee: zie
  // BZB_VULT_DE_LAAG.
  const noSoil = layers.filter((l) => isEmpty(l.soilType) && !isBzbLaag(l));
  if (noSoil.length) {
    add(BLOCKING, 'layer-no-soil', `${noSoil.length} ${noSoil.length === 1 ? 'laag' : 'lagen'} zonder grondsoort${ranges(noSoil)}`);
  }

  // Zonder barcode is een potje niet te koppelen aan de labuitslag. Dat is de kern van
  // herleidbaarheid; een monster dat je niet terugvindt is geen monster.
  const noBarcode = monsters.filter((m) => isEmpty(m.identificatie) && isEmpty(m.name));
  if (noBarcode.length) {
    add(BLOCKING, 'sample-no-barcode', `${noBarcode.length} grondmonster${noBarcode.length > 1 ? 's' : ''} zonder barcode`);
  }

  const bottlesNoBarcode = watermonsters
    .flatMap((w) => w.bottles || [])
    .filter((b) => isEmpty(b.barcode));
  if (bottlesNoBarcode.length) {
    add(BLOCKING, 'bottle-no-barcode', `${bottlesNoBarcode.length} fles${bottlesNoBarcode.length > 1 ? 'sen' : ''} zonder barcode`);
  }

  // Twee boringen met dezelfde naam maken de labkoppeling en de export dubbelzinnig: welke
  // van de twee hoort bij welk monster?
  if (!isEmpty(boring.name) && boring.name !== 'Naamloos' && ctx.duplicateNames?.has(boring.name)) {
    add(BLOCKING, 'duplicate-name', `Dubbele naam "${boring.name}"`);
  }

  // ── Aandacht ───────────────────────────────────────────────────────────────

  // Sleufmaten bij een inspectiegat met boring. Bij dit type IS de sleuf het meetpunt: zonder
  // lengte en breedte is niet vast te leggen hoeveel grond er is doorgezet, en TerraIndex
  // krijgt een inspectiegat zonder afmetingen. Niet blokkerend -- de export loopt door en het
  // profiel klopt -- maar het is bijna altijd vergeten, niet weggelaten.
  //
  // Bewust twee losse meldingen: je vult ze los in, en "sleufbreedte ontbreekt" stuurt je
  // preciezer dan "sleufmaten onvolledig".
  if (isInspectiegat(boring.type)) {
    if (num(boring.trialPitLengthM) === null) add(WARNING, 'no-trial-pit-length', 'Geen sleuflengte');
    if (num(boring.trialPitWidthM) === null) add(WARNING, 'no-trial-pit-width', 'Geen sleufbreedte');
  }

  // Een boring zonder naam is de stille uitkomst van een extractie die de aanhef niet verstond.
  // 'Naamloos' is geen naam maar een terugval, en zonder melding valt hij pas op bij de export
  // -- of helemaal niet, want de lijst toont gewoon een rij.
  if (isEmpty(boring.name) || String(boring.name).trim().toLowerCase() === 'naamloos') {
    add(WARNING, 'no-name', 'Geen naam');
  }

  if (isEmpty(boring.boormeester)) add(WARNING, 'no-boormeester', 'Geen boormeester');
  if (isEmpty(boring.datum)) add(WARNING, 'no-date', 'Geen datum');
  if (isEmpty(boring.referencePlane)) add(WARNING, 'no-reference-plane', 'Geen referentievlak');

  const noColour = layers.filter((l) => isEmpty(l.kleurMain));
  if (noColour.length && noColour.length !== layers.length) {
    // Alle lagen zonder kleur is meestal een bewuste keuze (of een ander onderzoekstype);
    // een paar lagen zonder kleur ziet er eerder uit als vergeten.
    add(WARNING, 'layer-no-colour', `${noColour.length} ${noColour.length === 1 ? 'laag' : 'lagen'} zonder kleur${ranges(noColour)}`);
  }

  for (const pb of peilbuizen) {
    if (num(pb.filterTopCm) === null || num(pb.filterBottomCm) === null) {
      add(WARNING, 'peilbuis-no-filter', `Peilbuis ${pb.name || ''} zonder filtertraject`.replace('  ', ' '));
      break;   // één melding per boring is genoeg om het op te pakken
    }
  }

  for (const w of watermonsters) {
    for (const [field, range] of Object.entries(PLAUSIBLE)) {
      const v = num(w[field]);
      if (v === null) continue;
      if (v < range.min || v > range.max) {
        add(WARNING, 'value-out-of-range', `${range.label} ${v} buiten bereik`);
      }
    }
  }

  return out;
}

/** Blokkerend eerst; binnen een niveau de volgorde waarin ze zijn gevonden. */
export function sortIssues(issues) {
  return [...(issues || [])].sort((a, b) =>
    (a.severity === BLOCKING ? 0 : 1) - (b.severity === BLOCKING ? 0 : 1));
}

/**
 * Problemen van een hele lijst boringen, met de boring erbij zodat een overzicht kan
 * verwijzen naar waar het opgelost moet worden.
 */
export function projectIssues(borings, ctx = {}) {
  const dupes = new Set();
  const seen = new Set();
  for (const b of borings || []) {
    const n = (b.name || '').trim();
    if (!n || n === 'Naamloos') continue;
    if (seen.has(n)) dupes.add(n);
    seen.add(n);
  }
  const out = [];
  for (const b of borings || []) {
    const issues = boringIssues(b, {
      duplicateNames: dupes,
      recordingStatus: ctx.recordingStatusFor?.(b) ?? null,
    });
    for (const i of issues) out.push({ ...i, boringId: b.id, boringName: b.name || 'Naamloos' });
  }
  return sortIssues(out);
}
